import { useState } from 'react'
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa'
import { motion } from 'framer-motion'
import { projectsData, Project } from '../../data'
import { FadeIn, StaggerContainer, StaggerItem } from '../animations/ScrollAnimations'
import FloatingParticles from '../animations/FloatingParticles'
import ProjectModal from './ProjectModal'

export default function Projects(): JSX.Element {
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const filteredProjects = selectedCategory === "All" 
    ? projectsData.projects 
    : projectsData.projects.filter(project => project.categories.includes(selectedCategory))

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedProject(null)
  }

  return (
    <div className="section bg-portfolio-black relative overflow-hidden">
      {/* Floating Particles Background */}
      <FloatingParticles />
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-portfolio-green/20 to-transparent"></div>
        <div className="absolute top-20 left-20 w-72 h-72 bg-portfolio-green/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-portfolio-green/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <FadeIn direction="up" delay={0.2}>
            <motion.h2 
              className="heading-2"
              whileHover={{ scale: 1.02 }}
            >
              {projectsData.title} <span className="gradient-text">{projectsData.subtitle}</span>
            </motion.h2>
            <motion.p 
              className="text-large max-w-3xl mx-auto"
              whileHover={{ scale: 1.01 }}
            >
              {projectsData.description}
            </motion.p>
          </FadeIn>
        </div>

        {/* Category Filter */}
        <motion.div 
          className="flex flex-wrap justify-center gap-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {projectsData.categories.map((category, index) => (
            <motion.button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-green-400 text-black'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
            >
              {category}
            </motion.button>
          ))}
        </motion.div>

        <StaggerContainer>
          <div className="grid-2">
            {filteredProjects.map((project, index) => (
              <StaggerItem key={index}>
                <motion.div 
                  className="card cursor-pointer group"
                  onClick={() => handleProjectClick(project)}
                  whileHover={{ 
                    scale: 1.03, 
                    y: -8,
                    boxShadow: "0 25px 50px rgba(149, 255, 49, 0.2)"
                  }}
                >
                  {/* Click indicator */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                    <div className="w-8 h-8 bg-green-400/20 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    </div>
                  </div>

                  

                  {/* Project Image */}
                  {project.image && (
                    <div className="mb-6 overflow-hidden rounded-lg">
                      <motion.img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-4">
                    <motion.h3 
                      className="heading-3 text-white"
                      whileHover={{ color: "#95FF31" }}
                    >
                      {project.title}
                    </motion.h3>
                    <div className="flex flex-wrap gap-1">
                      {project.categories.map((category, index) => (
                        <motion.span 
                          key={index}
                          className="px-2 py-1 bg-green-400/20 text-green-400 rounded-full text-xs"
                          whileHover={{ scale: 1.1 }}
                        >
                          {category}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                  
                  <motion.p 
                    className="text-gray-300 mb-6"
                    whileHover={{ x: 5 }}
                  >
                    {project.description}
                  </motion.p>
                  
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.technologies.map((tech, idx) => (
                      <motion.span
                        key={idx}
                        className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full text-sm border border-gray-700 cursor-pointer"
                        whileHover={{ 
                          scale: 1.1, 
                          backgroundColor: "#1a1a1a",
                          borderColor: "#95FF31",
                          color: "#95FF31"
                        }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>

                  <div className="flex gap-4">
                    {project.type === 'personal' && project.github && (
                      <motion.a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-gray-400 hover:text-green-400 transition-colors"
                        whileHover={{ scale: 1.05, x: 5 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaGithub className="text-lg" />
                        <span>Code</span>
                      </motion.a>
                    )}
                    {project.type === 'client' && (
                      <motion.span 
                        className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-medium border border-blue-500/30"
                        whileHover={{ scale: 1.1 }}
                      >
                        Client Project
                      </motion.span>
                    )}
                    {project.live && (
                      <motion.a
                        href={project.live}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-gray-400 hover:text-green-400 transition-colors"
                        whileHover={{ scale: 1.05, x: 5 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaExternalLinkAlt className="text-lg" />
                        <span>{project.type === 'client' ? 'View Project' : 'Live Demo'}</span>
                      </motion.a>
                    )}
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </div>
        </StaggerContainer>

        {/* Call to Action */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <motion.p 
            className="text-large mb-6"
            whileHover={{ scale: 1.02 }}
          >
            {projectsData.callToAction.text}
          </motion.p>
          <motion.a 
            href={projectsData.callToAction.href} 
            className="btn-primary"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            {projectsData.callToAction.buttonText}
          </motion.a>
        </motion.div>
      </div>

      {/* Project Modal */}
      <ProjectModal 
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  )
} 